import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import Breadcrumb from '@/components/common/Breadcrumb';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import ImagePreview from '@/components/common/media/ImagePreview';

import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import UploadField from '@/components/UI/UploadField';
import { useDebounce } from '@/hooks/useDebounce';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import {
  ICollection,
  ICollectionFormState,
} from '@/interfaces/collection.interface';
import { IMediaProp } from '@/interfaces/mediaResponse.interface';
import {
  IproductResponse,
  IproductResponseList,
} from '@/interfaces/productresponse.interface';
import ErrorMessage from '@/components/common/ErrorMessage';
import {
  GetFilterUrl,
  ProductStatus,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { errorTypes } from '@/utility/validation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { svgIcons } from '../../../components/UI/SvgIcons';
import { useNavigate, useParams } from 'react-router-dom';
import Pagination from '@/components/common/Pagination';

const { SearchIcon } = svgIcons;
interface ICollectionForm {
  title: string;
  description: string;
  price: string;
  file: File | null;
  isActive: boolean;
}

const CreateCollection = () => {
  const navigate = useNavigate();
  const [mediaFile, setMediaFile] = useState<IMediaProp | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const debounceValue = useDebounce(searchValue);
  const [quantities, setQuantities] = useState<
    Record<string, string | number | null>
  >({});
  const [states, setStates] = useState<ICollectionFormState>({
    products: [],
    selectedProducts: new Set(),
    isLoading: false,
    isUploadLoading: false,
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
  });
  const { id } = useParams();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ICollectionForm>({
    defaultValues: {
      title: '',
      description: '',
      price: '',
      file: null,
      isActive: true,
    },
  });

  const { selectedItems, handleSelectAll, handleSelectItem, bulkSelectItems } =
    useSelectableTable(states.products);
  useEffect(() => {
    if (!id) fetchProducts();
  }, [debounceValue, states.pageInfo.page, states.pageInfo.limit]);

  useEffect(() => {
    id && fetchCollectionInfo();
  }, [id, reset]);

  const columns: TableColumn<IproductResponse>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (item) => {
        const url = item?.directImageUrl
          ? item?.directImageUrl
          : item?.media?.[0]?.url;
        return (
          <ImagePreview
            src={`${API_URL_PRODUCT_SERVICE}/${url}`}
            alt={''}
            text={item.name}
          />
        );
      },
      className: 'p-3',
    },
    {
      key: 'stock',
      header: 'Unit Stock',
      render: (item) => item.productInfo[0]?.secondaryQuantity || 0,
    },
    {
      key: 'stock',
      header: 'CTN Stock',
      render: (item) => item.productInfo[0]?.primaryQuantity || 0,
    },
    {
      key: 'price',
      header: 'Price (BHD)',
      render: (item) => {
        const price =
          item.productInfo?.[0]?.secondaryPrice ||
          item.productInfo?.[0]?.price ||
          0;
        return <span>{toFixedThree(price)}</span>;
      },
    },
    {
      key: 'Quantity',
      header: 'Quantity',
      render: (item) => {
        const quantity = quantities[item.id] ?? 1;
        return (
          <div className="max-w-20">
            <FormField
              type="number"
              min={1}
              onWheel={(e) => e.currentTarget.blur()}
              value={quantity}
              onChange={(e) => {
                const value = e.target.value;
                setQuantities((prev) => ({
                  ...prev,
                  [item.id]: value,
                }));
              }}
              className="w-fit"
            />
          </div>
        );
      },
    },
  ];

  const fetchProducts = async () => {
    try {
      const filterQuery = {
        search: debounceValue,
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        status: ProductStatus.ACTIVE,
      };

      const query = GetFilterUrl(filterQuery);
      setStates((pre) => ({
        ...pre,
        isLoading: true,
      }));
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}?${query ? `${query}` : ''}`;
      const res = await callApi<IproductResponseList>(PATH, { method: 'GET' });
      setStates((pre) => ({
        ...pre,
        products: res.data,
        pageInfo: {
          limit: res.pageInfo.limit,
          page: res.pageInfo.page,
          total: res.pageInfo.total,
          totalPages: res.pageInfo.totalPages,
        },
        isLoading: false,
      }));
    } catch (error) {
      setStates((pre) => ({
        ...pre,
        isLoading: false,
      }));
      console.log('error is', error);
    }
  };

  const onSubmit = async (data: ICollectionForm) => {
    const productIds = Array.from(selectedItems);
    if (!id && productIds.length < 2) {
      showToastNotification('info', 'Please select at least two product');
      return;
    }
    const invalidProduct = productIds.find((productId) => {
      const qty = Number(quantities[productId] ?? 1);
      return isNaN(qty) || qty < 1;
    });

    if (invalidProduct) {
      showToastNotification(
        'danger',
        'Product quantity must be at least 1 for all selected products',
      );
      return;
    }

    try {
      const postData = {
        title: data.title,
        price: data.price,
        description: data.description,
        isActive: data.isActive,
        items: productIds.map((productId) => ({
          productId,
          quantity: Number(quantities[productId]) ?? 1,
          required: true,
        })),
        ...(mediaFile?.id ? { mediaId: mediaFile.id } : {}),
      };
      let PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.bundles}`;
      if (id) PATH = `${PATH}/${id}`;
      await callApi(PATH, {
        method: id ? 'PATCH' : 'POST',
        data: postData,
      });
      showToastNotification(
        'success',
        id
          ? 'collection updated sucessfully'
          : 'collection created successfully',
      );
      navigate(RoutesLink.collection);
    } catch (error) {
      console.error('error', error);
    }
  };

  const fetchCollectionInfo = async () => {
    try {
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.bundles}/${id}`;
      const res = await callApi<ICollection>(PATH, { method: 'GET' });

      reset({
        title: res.title || '',
        description: res.description || '',
        price: res.price?.toString() || '',
        file: null,
        isActive: res.isActive,
      });

      if (res.media && res.media.length > 0) {
        setMediaFile({
          id: res.media[0].id,
          url: `${res.media[0].url}`,
          type: res.media[0].type,
          altText: res.media[0].altText || 'Collection Image',
        });
      }
      const qtyMap: Record<string, number> = {};
      bulkSelectItems(res.items.map((i) => i.product));
      res.items.forEach((item) => {
        qtyMap[item.product.id] = item.quantity;
      });

      setQuantities(qtyMap);
      setStates((prev) => ({
        ...prev,
        products: res.items.map((i) => i.product),
      }));
    } catch (err) {
      console.error('Error fetching collection info:', err);
    }
  };

  const breadcrumb = [
    { label: 'Collection', href: RoutesLink.collection },
    { label: ' Create Collection' },
  ];

  const handleChangePage = (page: number) => {
    setStates((prev) => ({
      ...prev,
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    }));
  };

  const handleChangePageSize = (limit: number) => {
    setStates((prev) => ({
      ...prev,
      pageInfo: {
        ...states.pageInfo,
        page: 1,
        limit: limit,
      },
    }));
  };
  return (
    <Layout>
      <Breadcrumb items={breadcrumb} />
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormField
                  label="Title"
                  type="text"
                  placeholder="Enter title"
                  {...register('title', {
                    ...errorTypes.required('Title'),
                  })}
                />
                <ErrorMessage message={errors.title?.message} />
              </div>
              <div>
                <FormField
                  prefix="BD"
                  label="Price"
                  type="number"
                  min={0.001}
                  onWheel={(e) => e.currentTarget.blur()}
                  step="0.001"
                  placeholder="price..."
                  {...register('price', {
                    ...errorTypes.required('Price'),
                  })}
                />
                <ErrorMessage message={errors.price?.message} />
              </div>
            </div>

            <div>
              <FormField
                type="textarea"
                label="Description"
                placeholder="Start describing the Collection..."
                rows={4}
                {...register('description', {
                  ...errorTypes.required('Description'),
                })}
              />
              <ErrorMessage message={errors.description?.message} />
            </div>

            <div>
              <h4 className="block text-sm font-medium text-gray-700 mb-2">
                Upload Collection Image
              </h4>
              <UploadField
                referenceType="bundle"
                referenceId={mediaFile?.referenceId}
                multiple={false}
                initialMedia={id && mediaFile ? [mediaFile] : []}
                accept="image/*"
                onChangeMedia={(media) => {
                  setMediaFile(media[0]);
                }}
              />
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-base font-bold text-gray-800 mb-6">
            {!id ? 'Add Products' : 'Products'}
          </h3>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5 border-b border-light-40">
            {!id && (
              <div className="relative flex-grow">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                  {SearchIcon}
                </span>
                <input
                  type="text"
                  placeholder="Search by name...."
                  value={searchValue}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 duration-200 outline-none placeholder-light-40"
                />
              </div>
            )}
            {/* <div className="flex items-center gap-2">
              <button className="text-base text-black p-3 bg-blue-300 hover:bg-primary-light rounded-2xl">
                Sort
              </button>
              <button className="text-base text-black p-3 bg-blue-300 hover:bg-primary-light rounded-2xl">
                Filter
              </button>
            </div> */}
          </div>
          <CommonTable
            columns={columns}
            data={states.products}
            showCheckbox={id ? false : true}
            selectedItems={selectedItems}
            onSelectAll={handleSelectAll}
            onSelectItem={handleSelectItem}
            getItemId={(item) => item.id}
            emptyMessage="No products found"
            loading={states.isLoading}
            className="overflow-x-hidden"
          />
          <Pagination
            paginationData={states.pageInfo}
            onPageChange={(page) => handleChangePage(page)}
            onPageSizeChange={handleChangePageSize}
          />
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate(-1)}
            type="button"
            className="btn-secondary"
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Save
          </button>
        </div>
      </form>
    </Layout>
  );
};

export default CreateCollection;
